/**
 * Write & send › why this InMail reads as it does (Sofia, Oct 22 2026: "intransparent"), and the
 * template for THIS person. Shows which library template the draft follows, which paragraphs the
 * AI wrote (everything not in the filled template), what it took from the profile, and what was
 * filled in by code. Picking another template redrafts this one person; "Role default" goes back
 * to the template set for the role in step 1.
 */
import { useEffect, useState } from 'react';
import { fetchEmailTemplates, type EmailTemplate } from '../../services/emailTemplatesApi';
import type { OutreachCandidate } from '../../types/taOutreach';
import { Badge } from './outreachUi';

const ROLE = 'role';
const SUBJECT_FROM = {
  template: 'from the template',
  default: 'the standard subject (InMail Library)',
  ai: 'written by the AI — the template has none and no standard subject is set',
} as const;
const norm = (s: string) => s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, '$1 $2').replace(/\s+/g, ' ').trim().toLowerCase();

/** The draft's paragraphs that are not in the filled template: what the AI wrote. */
export function aiParagraphs(body: string, templateText: string): string[] {
  const fixed = norm(templateText.replace(/\[\[(PERSONAL|PITCH|WHERE)\]\]/g, '\n'));
  return `${body || ''}`
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p && !fixed.includes(norm(p)));
}

export function DraftWhy({
  candidate: c,
  drafting = false,
  why = null,
  disabled,
  onPick,
}: {
  candidate: OutreachCandidate;
  /** A (re)draft is running: say so, with a running clock (Sofia, Oct 22 2026: "it takes too long"). */
  drafting?: boolean;
  /** Why a template switch writes it again instead of switching at once (Oct 27 2026). */
  why?: string | null;
  disabled: boolean;
  onPick: (template: { scope: 'company' | 'personal'; key: string } | null) => void;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!drafting) return;
    const from = Date.now();
    const t = window.setInterval(() => setSeconds(Math.round((Date.now() - from) / 1000)), 1000);
    return () => {
      window.clearInterval(t);
      setSeconds(0);
    };
  }, [drafting]);
  useEffect(() => {
    let alive = true;
    fetchEmailTemplates()
      .then((all) => alive && setTemplates(all.filter((t) => t.category === 'outreach-inmail' && t.active !== false)))
      .catch(() => alive && setTemplates([]));
    return () => {
      alive = false;
    };
  }, []);

  const d = c.draft;
  const value = c.inmailTemplate ? `${c.inmailTemplate.scope}:${c.inmailTemplate.key}` : ROLE;
  const ai = d?.templateText ? aiParagraphs(d.body, d.templateText) : [];
  const hooks = c.gap?.hooks || [];

  return (
    <div className="rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-3 py-2 text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2">
          <span className="text-xs text-[#707070]">Template</span>
          <select
            aria-label="Template for this person"
            className="h-7 rounded-md border border-solid border-[#E9EAEC] bg-white px-2 text-[13px]"
            value={value}
            disabled={disabled}
            onChange={(e) => {
              const [scope, key] = e.target.value.split(':');
              setPending(e.target.selectedOptions[0]?.text || null);
              onPick(e.target.value === ROLE ? null : { scope: scope as 'company' | 'personal', key });
            }}
          >
            <option value={ROLE}>Role default{!c.inmailTemplate && d?.templateLabel ? ` — ${d.templateLabel}` : ''}</option>
            {templates.map((t) => (
              <option key={t.id} value={`${t.scope}:${t.key}`}>
                {t.scope === 'company' ? 'Company' : 'Mine'}: {t.label}
              </option>
            ))}
          </select>
        </label>
        {drafting ? (
          <span role="status" className="flex items-center gap-1.5 text-xs font-semibold text-[#0072EA]">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-solid border-[#0072EA] border-t-transparent" />
            Writing the InMail{pending ? ` with “${pending.replace(/^(Company|Mine): /, '')}”` : ''}… {seconds > 0 ? `${seconds} s` : ''}
            {why && <span className="font-normal text-[#707070]">· {why}</span>}
          </span>
        ) : (
          <span className="text-xs text-[#707070]">Changing it writes this InMail again.</span>
        )}
        <button type="button" className="ml-auto text-xs font-semibold text-[#0072EA] underline" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide why' : 'Why this text?'}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          {!d?.templateText ? (
            <p className="m-0 text-[#707070]">
              This draft was written before the InMail tracked its template. Pick a template above (or Redraft) to see where each part comes from.
            </p>
          ) : (
            <>
              <p className="m-0">
                <Badge tone="grey">Template</Badge> “{d.templateLabel || d.templateKey}” ({d.templateScope === 'personal' ? 'yours' : 'company'}) — its wording is kept word
                for word. Filled in: name and role title (hiring database), salary range and job ad (Lever posting), your booking link (InMail Library).
              </p>
              {d.subjectFrom && (
                <p className="m-0">
                  <Badge tone="grey">Subject</Badge> {SUBJECT_FROM[d.subjectFrom]}
                  {d.source === 'edited' ? ' — edited since.' : '.'}
                </p>
              )}
              <div>
                <Badge tone="blue">AI wrote</Badge>
                {ai.length ? (
                  <ul className="m-0 mt-1 list-disc space-y-1 pl-5">
                    {ai.map((p) => (
                      <li key={p}>{p.replace(/\[([^\]\n]+)\]\([^)\s]+\)/g, '$1')}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="ml-1 text-[#707070]">nothing beyond the template.</span>
                )}
              </div>
              <div>
                <Badge tone="grey">From the profile</Badge>
                {hooks.length ? (
                  <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-[#707070]">
                    {hooks.map((h) => (
                      <li key={h}>“{h}”</li>
                    ))}
                  </ul>
                ) : (
                  <span className="ml-1 text-[#707070]">no specific facts found — the personal sentence stays general.</span>
                )}
              </div>
              {d.source === 'edited' && <p className="m-0 text-xs text-[#707070]">Edited by you since — what you changed counts as yours, not the AI’s.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
